import apiService from './api.js';
import config from '../utils/envLoader.js';

class VersionService {
    constructor() {
        this.currentVersion = process.env.REACT_APP_VERSION || config.version;
        this.currentBuildNumber = process.env.REACT_APP_BUILD_NUMBER || config.buildNumber;
        this.checkInterval = null;
        this.lastCheckTime = null;
        this.isChecking = false;

        // Configuration for version checking
        this.CHECK_INTERVAL_MINUTES = 1; // Check every 1 minute
        this.CHECK_TIMEOUT_MS = 10000; // 10 second timeout for version checks
    }

    /**
     * Get current app version information
     */
    getCurrentVersionInfo() {
        return {
            version: this.currentVersion,
            buildNumber: this.currentBuildNumber
        };
    }

    /**
     * Compare two version strings
     * Returns: -1 if v1 < v2, 0 if equal, 1 if v1 > v2
     */
    compareVersions(v1, v2) {
        const v1Parts = v1.split('.').map(Number);
        const v2Parts = v2.split('.').map(Number);

        const maxLength = Math.max(v1Parts.length, v2Parts.length);

        for (let i = 0; i < maxLength; i++) {
            const v1Part = v1Parts[i] || 0;
            const v2Part = v2Parts[i] || 0;

            if (v1Part < v2Part) return -1;
            if (v1Part > v2Part) return 1;
        }

        return 0;
    }

    /**
     * Compare build numbers (assuming they are numeric strings)
     */
    compareBuildNumbers(b1, b2) {
        const num1 = parseInt(b1, 10);
        const num2 = parseInt(b2, 10);

        if (isNaN(num1) || isNaN(num2)) {
            // If not numeric, do string comparison
            return b1.localeCompare(b2);
        }

        return num1 - num2;
    }

    /**
     * Check if app version is outdated compared to server version
     */
    async checkForUpdates() {
        if (this.isChecking) {
            console.log('Version check already in progress, skipping');
            return null;
        }

        // Safety check - don't perform version check if not authenticated
        if (!apiService.isAuthenticated()) {
            console.log('User not authenticated, skipping version check');
            return {
                success: false,
                error: 'Not authenticated',
                isOutdated: false
            };
        }

        this.isChecking = true;
        console.log('=== Version Check Started ===');
        console.log('Current app version:', this.currentVersion);
        console.log('Current build number:', this.currentBuildNumber);

        try {
            const response = await apiService.checkAppVersion();

            if (!response.success) {
                console.error('Version check failed:', response.error);
                return {
                    success: false,
                    error: response.error,
                    isOutdated: false
                };
            }

            const serverVersion = response.data;
            console.log('Server version info:', serverVersion);

            const versionComparison = this.compareVersions(this.currentVersion, serverVersion.version);
            const buildComparison = this.compareBuildNumbers(this.currentBuildNumber, serverVersion.buildNumber);

            const isOutdated = versionComparison < 0 ||
                (versionComparison === 0 && buildComparison < 0);

            console.log('Version comparison result:', {
                versionComparison,
                buildComparison,
                isOutdated
            });

            this.lastCheckTime = new Date();

            const result = {
                success: true,
                isOutdated,
                currentVersion: {
                    version: this.currentVersion,
                    buildNumber: this.currentBuildNumber
                },
                serverVersion: {
                    version: serverVersion.version,
                    buildNumber: serverVersion.buildNumber
                },
                checkTime: this.lastCheckTime
            };

            console.log('=== Version Check Completed ===');
            return result;

        } catch (error) {
            console.error('Version check error:', error);
            return {
                success: false,
                error: error.message,
                isOutdated: false
            };
        } finally {
            this.isChecking = false;
        }
    }

    /**
     * Start periodic version checking
     */
    startPeriodicCheck(onUpdateAvailable = null, onError = null) {
        //console.log('Starting periodic version checks every', this.CHECK_INTERVAL_MINUTES, 'minutes');

        // Clear any existing interval
        this.stopPeriodicCheck();

        // Don't perform immediate check to avoid blocking login
        // Wait for the first interval to start checking
        // console.log('Version checking will start in', this.CHECK_INTERVAL_MINUTES, 'minutes');

        // Set up periodic checks
        this.checkInterval = setInterval(async () => {
            try {
                const result = await this.checkForUpdates();
                if (result && result.success && result.isOutdated && onUpdateAvailable) {
                    onUpdateAvailable(result);
                } else if (result && !result.success && onError) {
                    onError(result.error);
                }
            } catch (error) {
                console.error('Periodic version check error:', error);
                if (onError) onError(error.message);
            }
        }, this.CHECK_INTERVAL_MINUTES * 60 * 1000);
    }    /**
     * Stop periodic version checking
     */
    stopPeriodicCheck() {
        if (this.checkInterval) {
            // console.log('Stopping periodic version checks');
            clearInterval(this.checkInterval);
            this.checkInterval = null;
        }
    }

    /**
     * Force reload the application
     */
    forceReload() {
        console.log('Forcing application reload for version update');
        window.location.reload(true);
    }

    /**
     * Get last check time
     */
    getLastCheckTime() {
        return this.lastCheckTime;
    }

    /**
     * Check if enough time has passed since last check
     */
    shouldPerformCheck() {
        if (!this.lastCheckTime) return true;

        const timeSinceLastCheck = Date.now() - this.lastCheckTime.getTime();
        const checkIntervalMs = this.CHECK_INTERVAL_MINUTES * 60 * 1000;

        return timeSinceLastCheck >= checkIntervalMs;
    }
}

// Create singleton instance
const versionService = new VersionService();

export default versionService;